<template>
  <div class="module">
    <!-- Avant la connexion et le formulaire : changer de navigateur après coup ferait
    perdre la session et la saisie. -->
    <InAppBrowserNotice />

    <!-- Auth gate -->
    <div v-if="!authStore.isAuthenticated" class="centered-block">
      <h2>Connexion requise</h2>
      <p v-if="pendingPaymentReturn">
        Votre paiement HelloAsso a bien été pris en compte. Connectez-vous avec le même compte
        pour confirmer votre inscription, inutile de payer à nouveau.
      </p>
      <p v-else>Un compte DartsHub est nécessaire pour s'inscrire, il permet de retrouver votre inscription, votre licence et les classements.</p>
      <button class="btn-submit" @click="authStore.openAuthModal()">Se connecter</button>
    </div>

    <template v-else>
      <div v-if="loadingEvent" class="loading">Chargement de l'événement...</div>

      <template v-else-if="event">
        <h2 class="page-title">Inscription au tournoi</h2>

        <!-- Toujours visible, même si les inscriptions sont fermées : on doit savoir à
        quoi on s'inscrit (ou pourquoi on ne peut pas) avant tout le reste. -->
        <div class="event-banner">
          <h3>{{ event.display_name || event.name }}</h3>
          <p v-if="event.event_date"><span class="icon">📅</span> {{ formatEventDateRange(event.event_date, event.event_date_end) }}</p>
          <p v-if="event.location"><span class="icon">📍</span> {{ event.location }}</p>
          <p v-if="event.club_name"><span class="icon">🏆</span> {{ event.club_name }}</p>
          <router-link :to="eventPath({ ...event, place: event.location })" class="event-banner-link">Voir la fiche du tournoi</router-link>
        </div>

        <div v-if="isDemo" class="demo-banner">
          <strong>🧪 Événement de démonstration</strong>
          <p>Le formulaire fonctionne exactement comme pour le vrai tournoi, mais aucun paiement n'est demandé :
            les inscriptions sont enregistrées directement et seront effacées après la démonstration.</p>
        </div>

        <!-- Inscriptions fermées -->
        <div v-if="event.registration_status === 'closed'" class="closed-banner">
          <strong>🔒 Inscriptions fermées</strong>
          <p>Les inscriptions pour ce tournoi sont actuellement fermées.</p>
        </div>
        <!-- Butoir de clôture automatique dépassée : même verrou côté serveur
        (cf. events/routes.py::register_and_pay), ici on l'explique au joueur. -->
        <div v-else-if="deadlinePassed(event)" class="closed-banner">
          <strong>🔒 Inscriptions clôturées</strong>
          <p>La date limite d'inscription ({{ formatDeadline(event.registration_deadline) }}) est dépassée.</p>
        </div>
        <!-- Aucun mode de paiement disponible (même verrou côté serveur, cf.
        events/routes.py::payment_availability) : lien d'inscription partagé trop tôt. -->
        <div v-else-if="!event.registration_open" class="closed-banner">
          <strong>🔒 Inscriptions pas encore ouvertes</strong>
          <p>L'organisateur n'a pas encore finalisé l'ouverture des inscriptions pour ce tournoi.</p>
        </div>

        <template v-else>
          <p v-if="event.registration_deadline" class="deadline-hint">
            ⏳ Inscriptions ouvertes jusqu'au {{ formatDeadline(event.registration_deadline) }}.
          </p>

          <!-- Inscriptions réservées aux licenciés -->
          <div v-if="LICENSED_ONLY" class="licensed-only-banner">
            <strong>{{ event.cup_scope ? 'Coupe : participation réservée aux licenciés' : 'Inscriptions réservées aux licenciés' }}</strong>
            <p v-if="event.cup_scope === 'ligue'">Réservée aux joueurs licenciés dans la ligue {{ event.ligue }} pour la saison
              en cours (règlement FFD).</p>
            <p v-else-if="event.cup_scope === 'comite'">Réservée aux joueurs licenciés dans le comité {{ event.comite }} pour la
              saison en cours (règlement FFD).</p>
            <p v-else>Seuls les joueurs disposant d'une licence valide peuvent s'inscrire<template v-if="event.cup_scope"> (règlement
                FFD : les non licenciés ne peuvent pas participer à une coupe)</template>.</p>
          </div>

          <!-- Tarifs -->
          <div class="pricing-info">
            <h4>Tarifs</h4>
            <div class="pricing-grid">
              <div v-if="isLocal || LICENSED_ONLY" class="pricing-item">
                <span>{{ eventTeamPrices ? 'Individuel, par joueur et par catégorie' : 'Par joueur et par catégorie' }}</span>
                <strong>{{ formatEuros(licensedRateCents) }}</strong>
              </div>
              <template v-else>
                <div class="pricing-item">
                  <span>Licencié ({{ eventTeamPrices ? 'individuel, ' : '' }}par catégorie)</span><strong>{{ formatEuros(licensedRateCents) }}</strong>
                </div>
                <div class="pricing-item">
                  <span>Non licencié ({{ eventTeamPrices ? 'individuel, ' : '' }}par catégorie)</span><strong>{{ formatEuros(unlicensedRateCents) }}</strong>
                </div>
              </template>
              <template v-if="eventTeamPrices">
                <div v-if="isLocal || LICENSED_ONLY" class="pricing-item">
                  <span>Double, par équipe et par catégorie</span><strong>{{ formatEuros(eventTeamPrices.licensed) }}</strong>
                </div>
                <template v-else>
                  <div class="pricing-item">
                    <span>Équipe licenciée (double)</span><strong>{{ formatEuros(eventTeamPrices.licensed) }}</strong>
                  </div>
                  <div class="pricing-item">
                    <span>Équipe non licenciée (double)</span><strong>{{ formatEuros(eventTeamPrices.unlicensed) }}</strong>
                  </div>
                </template>
              </template>
            </div>
            <p v-if="eventTeamPrices && !isLocal && !LICENSED_ONLY" class="payment-modes-hint">
              Équipe mixte : chaque joueur paie la moitié du prix d'équipe correspondant à son statut.
            </p>
            <p v-if="!isDemo && paymentModesLabel(event)" class="payment-modes-hint">💶 {{ paymentModesLabel(event) }}</p>
          </div>

          <!-- Mes inscriptions existantes pour cet événement -->
          <div v-if="!mergedMyInscriptions.length && !loadingEvent" class="info-block">
            Vous n'avez aucune inscription pour cet événement pour le moment.
          </div>

          <!-- Uniquement les catégories : pas de montants ni de totaux, trompeurs dès qu'une
          part de double est payée sur la ligne d'un partenaire déjà inscrit (Christophe
          Buisine, Open de comité Montpellier 2026 : 12 € affichés pour 18 € payés). -->
          <div v-if="mergedMyInscriptions.length" class="existing-inscriptions">
            <template v-for="section in inscriptionSections" :key="section.title">
              <div class="existing-header">
                <h4>{{ section.title }}</h4>
              </div>
              <div v-for="ins in section.rows" class="existing-item" :key="ins.id">
                <span class="existing-name">
                  {{ ins.firstname }} {{ ins.name }}
                  <!-- Statut licencié / club : sans objet sur un tournoi local, qui ne
                  vérifie aucune licence (cf. isLocal). -->
                  <small v-if="!isLocal && ins.club">({{ ins.club }})</small>
                </span>

                <!-- category_simple peut cumuler plusieurs catégories ("J,M" : junior + masculin). -->
                <span v-for="cat in splitCats(ins.category_simple)" class="existing-cat" :key="cat">
                  {{ getCategoryLabel(cat, SIMPLE_CATEGORIES) }}
                </span>
                <span v-for="cat in splitCats(ins.category_double)" class="existing-cat" :key="cat">
                  {{ getCategoryLabel(cat, DOUBLE_CATEGORIES) }}
                  <template v-if="getPartnerNameById(ins.doublette_id)">avec {{ getPartnerNameById(ins.doublette_id) }}</template>
                  <span v-else class="seeking">(cherche partenaire)</span>
                </span>
              </div>
            </template>
          </div>

          <!-- ── Écran 1 : catalogue + panier ────────────────────────────── -->
          <div v-if="step === 'form'">
            <div v-if="!visibleProducts.length" class="info-block">
              Aucune catégorie n'est ouverte aux inscriptions pour le moment.
            </div>
            <div v-else class="catalog">
              <h4 class="catalog-title">Ajouter au panier</h4>
              <div class="catalog-grid">
                <button v-for="p in visibleProducts" class="catalog-btn" @click="addCartItem(p)" :key="p.type">
                  <span class="catalog-icon">{{ p.icon }}</span>
                  <span class="catalog-label">{{ p.label }}</span>
                  <span class="catalog-add">+ Ajouter</span>
                </button>
              </div>
            </div>

            <div v-if="!cartItems.length" class="info-block">
              Votre panier est vide. Choisissez un type d'inscription ci-dessus pour commencer.
            </div>

            <div v-if="cartItems.length" id="cart" class="cart-list">
              <h4 class="catalog-title">🛒 Panier</h4>
              <div v-for="(item, index) in cartItems" class="cart-line" :key="item.id">
                <div class="cart-line-info" @click="openItemEditor(index)">
                  <span class="cart-line-title">{{ item.product.icon }} {{ cartItemNames(item) }}</span>
                  <span class="cart-line-detail">
                    {{ item.product.label }}<template v-if="cartItemCategories(item)"> · {{ cartItemCategories(item) }}</template>
                    · {{ getCartItemPrice(item) }} €
                  </span>
                  <span v-if="cartItemError(item)" class="cart-line-warning">⚠️ {{ cartItemError(item) }}</span>
                </div>
                <div class="cart-line-actions">
                  <button class="btn-secondary" @click="openItemEditor(index)">Modifier</button>
                  <button class="btn-remove" @click="removeCartItem(index)">Supprimer</button>
                </div>
              </div>
            </div>

            <!-- Saisie d'une inscription du panier dans une modale : plus besoin de remonter au
            catalogue puis redescendre à chaque ajout. -->
            <div v-if="editingItem" class="modal-overlay item-modal-overlay" @click.self="cancelItemEditor">
              <div class="modal-content item-modal">
                <template v-for="item in [editingItem]" :key="item.id">
                  <h4 class="item-modal-title">{{ item.product.icon }} {{ item.product.label }}</h4>

              <div v-for="(slot, slotIndex) in item.slots" class="cart-slot" :key="slotIndex">
                <label v-if="item.slots.length > 1" class="slot-label">
                  {{ slotIndex === 0 ? 'Joueur' : 'Partenaire' }}
                </label>
                <button v-if="myLicense && item.product.slotsLicensedHint[slotIndex] && !item.slots.some(isMe)
                  && (item.product.isDouble ? !myExisting.double.size : myRemainingSimple.length > 0)" type="button"
                  class="btn-secondary its-me-btn" @click="fillWithMe(item, slotIndex)">
                  🙋 C'est moi
                </button>
                <PlayerPicker v-model:name="slot.name" v-model:firstname="slot.firstname"
                  :remote="canSearchLicenses(item, slotIndex)"
                  :remoteResults="licenseSuggestions[slotKey(item, slotIndex)]"
                  :remoteHint="licenseSearchHint"
                  @search="(q: string) => searchLicensesFor(slotKey(item, slotIndex), q)"
                  @pick="(lic: RosterEntry) => onPick(item, slotIndex, lic)" />
                <!-- Joueur saisi sans choisir de licence dans les suggestions : licencié ou
                non, réponse obligatoire (sinon un licencié pressé paierait le tarif non
                licencié). Licencié : numéro puis club à valider (cf. OtherLigueClub). -->
                <div v-if="licenseQuestionShown(slot)" class="license-question">
                  <p class="license-question-title">
                    Licence FFD {{ currentSeason }} pour {{ slot.firstname.trim() }} {{ slot.name.trim() }} ? *
                  </p>
                  <label class="license-question-option">
                    <input type="radio" :name="`license-${item.id}-${slotIndex}`" :checked="slot.licenseAnswer === 'no'"
                      @change="setLicenseAnswer(slot, 'no')" />
                    Non licencié(e)
                  </label>
                  <label class="license-question-option">
                    <input type="radio" :name="`license-${item.id}-${slotIndex}`" :checked="slot.licenseAnswer === 'yes'"
                      @change="setLicenseAnswer(slot, 'yes')" />
                    Licencié(e)
                  </label>
                  <OtherLigueClub v-if="slot.licenseAnswer === 'yes'"
                    v-model:licenseNumber="slot.otherLicenseNumber" v-model:clubId="slot.otherClubId"
                    v-model:clubName="slot.otherClubName" />
                </div>
                <!-- Nécessaire pour vérifier l'éligibilité à la Double Féminine (réservée
                aux femmes) -- masqué dès qu'un licencié est choisi ci-dessus (sa licence fait
                foi). Sur un tournoi local, seulement demandé quand la Double Féminine est
                choisie (le backend n'y vérifie pas le genre, mais le formulaire ne doit pas
                exiger un champ qu'il n'affiche pas). -->
                <div v-if="item.product.isDouble && !slot.licensePicked && (!isLocal || item.categoryDouble === 'DF')"
                  class="form-group gender-group">
                  <label>Genre{{ item.categoryDouble === 'DF' ? ' *' : '' }}</label>
                  <select v-model="slot.gender" class="form-input">
                    <option value="">--</option>
                    <option value="masculin">Masculin</option>
                    <option value="féminin">Féminin</option>
                  </select>
                </div>
              </div>

              <!-- Genre : nécessaire pour vérifier l'éligibilité aux catégories individuelles
              (Masculin, Vétéran, Féminin, Junior) quand le joueur n'est pas licencié --
              obligatoire pour Masculin et Féminin (sans genre, Vétéran/Junior restent
              simplement grisés). Date de naissance : utile seulement pour
              Vétéran/Junior (cf. _calculate_category côté backend). Les deux sont masqués
              dès qu'un licencié a été choisi ci-dessus, seule sa licence fait foi dans ce
              cas. Un tournoi local ne vérifie plus l'éligibilité par âge/genre : seul le
              genre y est demandé, et uniquement pour la catégorie Féminin. -->
              <div v-if="!isLocal && !item.product.isDouble && !item.slots[0].licensePicked" class="form-row birth-gender-row">
                <div class="form-group">
                  <label>Date de naissance{{ isSimpleCatSelected(item.slots[0], 'V') || isSimpleCatSelected(item.slots[0], 'J') ? ' *' : ' (si Vétéran/Junior)' }}</label>
                  <input v-model="item.slots[0].birth_date" type="date" class="form-input" />
                </div>
                <div class="form-group">
                  <label>Genre{{ isSimpleCatSelected(item.slots[0], 'F') || isSimpleCatSelected(item.slots[0], 'M') ? ' *' : '' }}</label>
                  <select v-model="item.slots[0].gender" class="form-input">
                    <option value="">--</option>
                    <option value="masculin">Masculin</option>
                    <option value="féminin">Féminin</option>
                  </select>
                </div>
              </div>
              <div v-else-if="isLocal && !item.product.isDouble && isSimpleCatSelected(item.slots[0], 'F')"
                class="form-row birth-gender-row">
                <div class="form-group">
                  <label>Genre *</label>
                  <select v-model="item.slots[0].gender" class="form-input">
                    <option value="">--</option>
                    <option value="masculin">Masculin</option>
                    <option value="féminin">Féminin</option>
                  </select>
                </div>
              </div>

              <!-- Catégorie individuelle -->
              <CategorySelector v-if="!item.product.isDouble" v-model="item.slots[0].category_simple"
                :categories="availableSimpleCategories" title="Catégorie individuelle" :multiple="true"
                :disabledValues="fullSimpleCategories" :ineligibleReasons="blockedSimpleCategories(item)"
                :isCumulable="simpleCumulable(item)">
                <template v-if="!isLocal">
                  <p v-if="isSimpleCatSelected(item.slots[0], 'J') && isSimpleCatSelected(item.slots[0], 'M')"
                    class="cat-hint">
                    Junior + Masculin sélectionnés
                  </p>
                  <p v-if="isSimpleCatSelected(item.slots[0], 'J') && isSimpleCatSelected(item.slots[0], 'F')"
                    class="cat-hint">
                    Junior + Féminine sélectionnées
                  </p>
                  <p class="cat-hint">
                    Une seule catégorie individuelle (sauf junior : Masculin en plus pour un garçon,
                    Féminin en plus pour une fille).
                  </p>
                </template>
                <p v-else class="cat-hint">Une seule catégorie individuelle à la fois.</p>
              </CategorySelector>

              <!-- Catégorie double -->
              <CategorySelector v-else v-model="item.categoryDouble" :categories="availableDoubleCategories"
                title="Catégorie double" :multiple="false" :disabledValues="fullDoubleCategories"
                :ineligibleReasons="blockedDoubleCategories(item)" />

              <div class="player-price">
                Tarif estimé : <strong>{{ getCartItemPrice(item) }} €</strong>
              </div>
                  <div class="form-actions item-modal-actions">
                    <button class="btn-secondary" @click="cancelItemEditor">{{ editingIsNew ? 'Annuler' : 'Fermer' }}</button>
                    <button class="btn-submit" :disabled="!hasAllowedCategory(item)"
                      :title="hasAllowedCategory(item) ? undefined : 'Choisissez une catégorie disponible'"
                      @click="confirmItemEditor">
                      {{ editingIsNew ? 'Ajouter au panier' : 'Enregistrer' }}
                    </button>
                  </div>
                </template>
              </div>
            </div>

            <div v-if="generalError" class="error-block">
              <p>{{ generalError }}</p>
            </div>

            <div class="form-actions back-actions">
              <router-link to="/calendar">
                <button class="btn-secondary">Retour au calendrier</button>
              </router-link>
            </div>

            <!-- Barre panier toujours visible en bas d'écran -->
            <div v-if="cartItems.length" class="cart-bar">
              <button class="cart-bar-summary" @click="scrollToCart">
                🛒 {{ cartItems.length }} inscription{{ cartItems.length > 1 ? 's' : '' }} ·
                <strong>{{ totalPrice }} €</strong>
                <small v-if="!isLocal"> (Estimation avant vérification)</small>
              </button>
              <button class="btn-submit" :disabled="submitting" @click="validateRegistration">
                {{ submitting ? 'Vérification...' : 'Vérifier les inscriptions' }}
              </button>
            </div>
          </div>

          <!-- ── Écran 2 : récapitulatif ───────────────────────────────── -->
          <div v-else-if="step === 'review' && reviewData?.results?.length" class="summary">
            <h3>Récapitulatif des inscriptions</h3>

            <!-- Une ligne par joueur, même inscrit dans plusieurs catégories ou comme
            partenaire d'une doublette (cf. summaryPlayers). -->
            <div v-for="player in summaryPlayers"
              :class="['summary-player', { 'already-existed': player.alreadyExisted }]"
              :key="player.key">
              <div class="person-row">
                <div class="person-info">
                  <strong>{{ player.firstname }} {{ player.name }}</strong>
                </div>
                <div class="person-status">
                  <span v-if="player.alreadyExisted" class="license-tag already-tag">
                    Déjà inscrit
                  </span>
                  <template v-else>
                    <span v-if="!isLocal" :class="player.is_licensed ? 'license-tag found' : 'license-tag not-found'">
                      {{ player.is_licensed ? 'Licencié' : 'Non licencié' }}<span v-if="player.club"> ({{ player.club }})</span>
                    </span>
                    <strong class="person-price">{{ player.total }} €</strong>
                  </template>
                </div>
              </div>

              <ul class="person-entries">
                <li v-for="(entry, i) in player.entries" :key="i">
                  {{ entry.label }}<template v-if="entry.partner"> avec {{ entry.partner }}</template>
                  <template v-if="!player.alreadyExisted && player.entries.length > 1"> · {{ entry.price }} €</template>
                </li>
              </ul>

              <div v-for="(w, i) in player.warnings" class="warning" :key="`w${i}`">
                {{ w }}
              </div>
            </div>

            <!-- Total global -->
            <div class="total-price summary-total">
              <span>Total à régler :</span>
              <strong>{{ calculateGlobalTotal }} €</strong>
            </div>

            <!-- Messages globaux -->
            <div v-if="(nonLicensedNames.length || reviewData?.licensed_only_error) && LICENSED_ONLY" class="error-block">
              <p v-if="reviewData?.licensed_only_error">{{ reviewData.licensed_only_error }}</p>
              <p v-else>Les inscriptions sont actuellement réservées aux licenciés. Joueur(s) non licencié(s) : {{
                nonLicensedNamesDisplay }}.</p>
              <p v-if="event.contact_email">Si vous pensez qu'il s'agit d'une erreur, contactez l'organisateur :
                <a :href="`mailto:${event.contact_email}`">{{ event.contact_email }}</a>.</p>
            </div>
            <div v-else-if="nonLicensedNames.length && !LICENSED_ONLY" class="warning-block">
              <p>Joueur(s) non licencié(s) détecté(s) : {{ nonLicensedNamesDisplay }}. Le tarif non licencié
                ({{ formatEuros(unlicensedRateCents) }}) sera appliqué.
              </p>
              <p v-if="event.contact_email">Si vous pensez qu'il s'agit d'une erreur, contactez l'organisateur :
                <a :href="`mailto:${event.contact_email}`">{{ event.contact_email }}</a>.</p>
            </div>
            <div v-else-if="generalError" class="error-block">
              <p>{{ generalError }}</p>
            </div>
            <div v-if="successMessage" class="success-block">
              <p>{{ successMessage }}</p>
              <p v-if="alreadyExisted.length">
                Déjà inscrits : {{ alreadyExisted.join(', ') }}
              </p>
            </div>

            <div class="form-actions">
              <button class="btn-secondary" @click="goBackToForm">Modifier</button>
              <template v-if="players.length > 0">
                <button v-if="isDemo" class="btn-submit" :disabled="!canSubmitRegistration"
                  @click="payAndRegister('online')">
                  {{ submitting ? 'Enregistrement...' : `Valider (démo : ${formatEuros(calculateGlobalTotal * 100)} non encaissés)` }}
                </button>
                <template v-else>
                  <button v-if="event.online_payment_available" class="btn-submit" :disabled="!canSubmitRegistration"
                    @click="payAndRegister('online')">
                    {{ submitting && submittingMode === 'online' ? 'Redirection...'
                      : `Payer ${formatEuros(calculateGlobalTotal * 100)} en ligne et valider` }}
                  </button>
                  <!-- Secondaire quand le paiement en ligne est aussi proposé : l'organisateur
                  préfère généralement être payé d'avance. -->
                  <button v-if="event.onsite_payment_available"
                    :class="event.online_payment_available ? 'btn-secondary' : 'btn-submit'"
                    :disabled="!canSubmitRegistration" @click="payAndRegister('onsite')">
                    {{ submitting && submittingMode === 'onsite' ? 'Enregistrement...'
                      : calculateGlobalTotal > 0 ? `Valider, je paierai ${formatEuros(calculateGlobalTotal * 100)} sur place`
                      : 'Valider mon inscription' }}
                  </button>
                </template>
              </template>
            </div>

            <div v-if="players.length === 0 && !nonLicensedNames.length && reviewData?.can_submit !== false"
              class="info-block all-registered">
              Tous les joueurs sont déjà inscrits à cet événement.
            </div>
          </div>

          <!-- ── Écran 3 : confirmation finale ──────────────────────────── -->
          <!-- Atteint après retour de paiement HelloAsso confirmé (voir onMounted), ou
               directement pour une inscription à régler sur place. -->
          <div v-else-if="step === 'confirmed'" class="confirmed-step">
            <div class="success-banner">
              <h3>Inscription confirmée !</h3>
              <p v-if="onsiteDue > 0">
                💶 <strong>{{ onsiteDue }} €</strong> à régler sur place, auprès de l'organisateur, le jour du tournoi.
              </p>
            </div>
            <!-- Message libre de l'organisateur (horaires de pointage, consignes...), saisi
                 dans les paramètres du tournoi, cf. Event.confirmation_message. -->
            <div v-if="event.confirmation_message" class="confirmation-message">
              <p><template v-for="(seg, i) in linkify(event.confirmation_message)" :key="i"><a
                    v-if="seg.type === 'link'" :href="seg.href" target="_blank" rel="noopener noreferrer">{{ seg.value }}</a><template
                    v-else>{{ seg.value }}</template></template></p>
            </div>
            <div class="form-actions">
              <router-link to="/calendar">
                <button class="btn-secondary">Retour au calendrier</button>
              </router-link>
            </div>
          </div>

        </template> <!-- /registrations_open -->
      </template>

      <div v-else class="no-events">
        <p>Événement introuvable.</p>
        <router-link to="/calendar"><button>Retour au calendrier</button></router-link>
      </div>
    </template> <!-- /auth -->
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { calculateFfdCategory, comparePlayerNames, eligibleSimpleCategories, existingCategoriesForPlayer, formatEventDateRange, ineligibleDoubleCategories, normalize, normalizeFirstname, wouldViolateSimpleCumul } from '@/functions/utils'
import { deadlinePassed, doubleShareCents, eventPath, formatDeadline, parseEventId, paymentModesLabel, teamPrices } from '@/functions/eventDisplay'
import { linkify } from '@/functions/linkify'
import { useRoute } from 'vue-router'
import backendApi from '@/axios/backendApi'
import { toast } from 'vue3-toastify'
import { useAuthStore } from '@/stores/useAuthStore'
import { getCurrentSeason } from '@/config/config'
import { useSeasonStore } from '@/stores/useSeasonStore'
import CategorySelector from '@/components/CategorySelector.vue'
import InAppBrowserNotice from '@/components/InAppBrowserNotice.vue'
import PlayerPicker, { type RosterEntry } from '@/components/PlayerPicker.vue'
import OtherLigueClub from '@/components/OtherLigueClub.vue'

const route = useRoute()
const authStore = useAuthStore()

// licensed_only_required : statut "licensed_only" OU coupe FFD/Ligue/Comité (imposé par le
// règlement, calculé par le back -- cf. events/routes.py::licensed_only_enforced).
const LICENSED_ONLY = computed(() =>
  event.value?.registration_status === 'licensed_only' || !!event.value?.licensed_only_required
)

const SIMPLE_CATEGORIES = [
  { value: 'M', label: 'Ind. Masculin' },
  { value: 'V', label: 'Ind. Vétéran' },
  { value: 'F', label: 'Ind. Féminin' },
  { value: 'J', label: 'Ind. Junior' },
]

const DOUBLE_CATEGORIES = [
  { value: 'DM', label: 'Double' },
  { value: 'DF', label: 'Dbl. Féminin' },
]

interface PlayerEntry {
  name: string
  firstname: string
  category_simple: string | null
  category_double: string | null
  other_ligue: boolean
  // Réponse à "Licence FFD ?" (cf. CartSlot.licenseAnswer), puis numéro et club validé
  // pour un licencié (cf. OtherLigueClub).
  license_answer: string
  license_number: string
  club_id: number | null
  club_name: string
  partner_name: string
  partner_firstname: string
  partner_other_ligue: boolean
  partner_license_answer: string
  partner_license_picked: boolean
  partner_license_number: string
  partner_club_id: number | null
  partner_club_name: string
  partner_gender: string | null
  birth_date: string | null
  gender: string | null
  // Licencié choisi dans le picker : sa licence fait foi pour Vétéran/Junior, la date
  // de naissance (jamais exposée par registration_license_search) n'est pas exigée.
  license_picked: boolean
}

const createEmptyPlayer = (): PlayerEntry => ({
  name: '',
  firstname: '',
  category_simple: null,
  category_double: null,
  other_ligue: false,
  license_answer: '',
  license_number: '',
  club_id: null,
  club_name: '',
  partner_name: '',
  partner_firstname: '',
  partner_other_ligue: false,
  partner_license_answer: '',
  partner_license_picked: false,
  partner_license_number: '',
  partner_club_id: null,
  partner_club_name: '',
  partner_gender: null,
  birth_date: null,
  gender: null,
  license_picked: false,
})

// ── Panier : "produits" d'inscription plutôt qu'une liste de blocs joueur vierges --
// chaque bouton ajoute une ligne déjà typée (nb de joueurs). Le statut licencié n'est
// jamais demandé : il est détecté par le backend à l'étape "Vérifier" (recherche
// nationale, pas limitée au club) ; le picker ne sert qu'à fiabiliser la saisie et
// prérenseigner un prix estimé plus juste, la saisie libre reste toujours possible.
interface ProductDef {
  type: string
  label: string
  icon: string
  isDouble: boolean
  slotsLicensedHint: boolean[]
}

const PRODUCTS: ProductDef[] = [
  { type: 'individuel', label: 'Individuel', icon: '🎯', isDouble: false, slotsLicensedHint: [true] },
  { type: 'doublette', label: 'Doublette', icon: '👥', isDouble: true, slotsLicensedHint: [true, true] },
]

// Un tournoi local n'a pas de distinction licencié/non licencié -- deux produits
// seulement, slotsLicensedHint toujours à false pour ne jamais proposer le picker de
// licence (cf. PlayerPicker :roster ci-dessous, qui ne s'affiche que si true).
const PRODUCTS_LOCAL: ProductDef[] = [
  { type: 'individuel', label: 'Individuel', icon: '🎯', isDouble: false, slotsLicensedHint: [false] },
  { type: 'doublette', label: 'Doublette', icon: '👥', isDouble: true, slotsLicensedHint: [false, false] },
]

interface CartSlot {
  name: string
  firstname: string
  category_simple: string | null
  licensePicked: boolean
  // Réponse à "Licence FFD ?" pour un joueur saisi sans choisir de licence (cf.
  // licenseQuestionShown) : '' tant qu'il n'a pas répondu. otherLigue en découle (oui).
  licenseAnswer: '' | 'yes' | 'no'
  otherLigue: boolean
  // Avec otherLigue : numéro de licence saisi et club validé (cf. OtherLigueClub) --
  // otherClubId vide pour un club saisi à la main.
  otherLicenseNumber: string
  otherClubId: number | null
  otherClubName: string
  // Catégorie FFD (M/F/JG/JF/V) de la licence choisie -- fait foi sur birth_date/gender
  // ci-dessous tant qu'un licencié est sélectionné (cf. onPick).
  licenseCategory: string | null
  birth_date: string | null
  gender: string | null
}

interface CartItem {
  id: string
  product: ProductDef
  slots: CartSlot[]
  categoryDouble: string | null
}

const createSlot = (): CartSlot => ({
  name: '', firstname: '', category_simple: null, licensePicked: false, licenseAnswer: '', otherLigue: false,
  otherLicenseNumber: '', otherClubId: null, otherClubName: '',
  licenseCategory: null, birth_date: null, gender: null,
})

// Persisté en sessionStorage : le paiement HelloAsso quitte complètement la page
// (window.location.href), donc l'état en mémoire de la SPA est perdu au retour
// (paiement annulé, retour arrière...) sans ça -- tout le panier saisi serait à
// refaire à chaque tentative de paiement interrompue.
const CART_STORAGE_KEY = `compet_cart_${parseEventId(route.params.eventId)}`

const loadPersistedCart = (): CartItem[] => {
  try {
    const raw = sessionStorage.getItem(CART_STORAGE_KEY)
    // Champs ajoutés depuis l'enregistrement du panier : complétés par leurs défauts.
    return raw
      ? JSON.parse(raw).map((item: CartItem) => ({ ...item, slots: item.slots.map(sl => ({ ...createSlot(), ...sl })) }))
      : []
  } catch {
    return []
  }
}

const cartItems = ref<CartItem[]>(loadPersistedCart())
let cartIdSeq = cartItems.value.length

watch(cartItems, (val) => {
  try {
    if (val.length) sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(val))
    else sessionStorage.removeItem(CART_STORAGE_KEY)
  } catch { /* stockage indisponible (navigation privée...) -- pas bloquant */ }
}, { deep: true })

const clearPersistedCart = () => {
  try { sessionStorage.removeItem(CART_STORAGE_KEY) } catch { /* pas bloquant */ }
  cartItems.value = []
}

const addCartItem = (product: ProductDef) => {
  const slots = product.slotsLicensedHint.map(() => createSlot())
  // Joueur 1 prérempli avec le compte connecté, seulement pour la toute première
  // ligne individuelle ajoutée -- reprend l'ancien comportement (le joueur 1 par
  // défaut, c'est "moi"). Pas si je n'ai plus aucune catégorie individuelle à prendre
  // (déjà inscrit), même règle que le bouton "C'est moi".
  const meAlreadyDone = myLicense.value
    ? myRemainingSimple.value.length === 0
    : existingCategoriesForPlayer(allEventInscriptions.value, authStore.name ?? '', authStore.firstname ?? '').simple.size > 0
  const prefillMe = cartItems.value.length === 0 && slots.length === 1 && authStore.isAuthenticated && !meAlreadyDone
  if (prefillMe && !myLicense.value && authStore.name && authStore.firstname) {
    slots[0].name = authStore.name.toUpperCase()
    slots[0].firstname = authStore.firstname
  }
  const item: CartItem = { id: `c${cartIdSeq++}`, product, slots, categoryDouble: null }
  cartItems.value.push(item)
  openItemEditor(cartItems.value.length - 1, true)
  // Avec une licence liée au compte, "moi" est prérempli comme un choix du picker
  // (catégorie, genre, date de naissance) -- seulement sur un emplacement licencié.
  if (prefillMe && myLicense.value && product.slotsLicensedHint[0]) {
    fillWithMe(cartItems.value[cartItems.value.length - 1], 0)
  }
}

const removeCartItem = (index: number) => {
  cartItems.value.splice(index, 1)
  editingIndex.value = null
}

// ── Modale de saisie d'une inscription du panier ──────────────────────────────
const editingIndex = ref<number | null>(null)
// Vrai tant que l'inscription vient d'être ajoutée et n'a jamais été validée : "Annuler"
// la retire alors du panier plutôt que de la laisser à moitié remplie.
const editingIsNew = ref(false)
const editingItem = computed(() => (editingIndex.value === null ? null : cartItems.value[editingIndex.value] ?? null))

// Page derrière bloquée tant que la modale est ouverte : seul le fond de la modale défile.
watch(editingItem, (item) => {
  document.body.style.overflow = item ? 'hidden' : ''
})
onUnmounted(() => {
  document.body.style.overflow = ''
})

const openItemEditor = (index: number, isNew = false) => {
  editingIndex.value = index
  editingIsNew.value = isNew
}

const confirmItemEditor = () => {
  if (!editingItem.value) return
  const error = cartItemError(editingItem.value)
  if (error) {
    toast.error(error.charAt(0).toUpperCase() + error.slice(1))
    return
  }
  editingIndex.value = null
}

const cancelItemEditor = () => {
  if (editingIsNew.value && editingIndex.value !== null) cartItems.value.splice(editingIndex.value, 1)
  editingIndex.value = null
}

// Doublette : nom puis prénom par ordre alphabétique (cf. comparePlayerNames).
const cartItemNames = (item: CartItem) =>
  [...item.slots].sort(comparePlayerNames).map(sl => `${sl.firstname} ${sl.name}`.trim()).filter(Boolean).join(' / ') || 'À compléter'

const cartItemCategories = (item: CartItem) => {
  if (item.product.isDouble) {
    return item.categoryDouble ? getCategoryLabel(item.categoryDouble, DOUBLE_CATEGORIES) : ''
  }
  return (item.slots[0].category_simple ?? '').split(',').filter(c => c.trim())
    .map(c => getCategoryLabel(c, SIMPLE_CATEGORIES)).join(' + ')
}

// Au moins une catégorie cochée, et aucune bloquée (déjà inscrit, non éligible...) :
// sinon le bouton d'enregistrement de la modale n'est pas cliquable.
const hasAllowedCategory = (item: CartItem): boolean => {
  const selected = item.product.isDouble ? item.categoryDouble : item.slots[0].category_simple
  return !!selected && !blockedSelectionError(item)
}

const cartItemError = (item: CartItem) => playerError(cartItemToPlayer(item)) ?? blockedSelectionError(item)

const scrollToCart = () => document.getElementById('cart')?.scrollIntoView({ behavior: 'smooth', block: 'start' })

// ── "Moi" : licence de la saison en cours liée au compte connecté (GET /licenses/me) --
// remplit un emplacement exactement comme un choix dans le picker (catégorie, genre et
// date de naissance issus de la licence).
const myLicense = ref<RosterEntry | null>(null)

const loadMyLicense = async () => {
  try {
    const { data } = await backendApi.get('/licenses/me')
    myLicense.value = (data ?? []).find((l: any) => l.season === getCurrentSeason()) ?? null
  } catch {
    myLicense.value = null
  }
  if (!myLicense.value) myLicense.value = await loadMyPendingRequest()
}

// Pas encore de licence de la saison mais une demande transmise au comité : déjà licencié
// pour l'inscription (cf. Dartshub events/routes.py::_current_season_licensed_names),
// "C'est moi" doit donc aussi la proposer (Michel Vanhoeserlande, 2026-11-01). Une demande
// encore en base est toujours de la saison en cours (supprimée à sa validation). Demande
// dirigeant exclue : aucun droit de jeu. Catégorie calculée comme à la validation.
const loadMyPendingRequest = async (): Promise<RosterEntry | null> => {
  try {
    const { data } = await backendApi.get('/licenses/requests/me')
    const req = (data ?? []).find((r: any) => r.submitted_to_committee && !r.fonction_dirigeante)
    if (!req) return null
    return {
      name: req.name, firstname: req.firstname, gender: req.gender, birth_date: req.birth_date,
      category: calculateFfdCategory(req.gender, req.birth_date, getCurrentSeason()),
      club_name: req.club_name,
    }
  } catch {
    return null
  }
}

const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()
const isMe = (slot: CartSlot) =>
  !!myLicense.value && sameName(slot.name, myLicense.value.name) && sameName(slot.firstname, myLicense.value.firstname)
// Catégories où je suis DÉJÀ inscrit pour cet événement.
const myExisting = computed(() => myLicense.value
  ? existingCategoriesForPlayer(allEventInscriptions.value, myLicense.value.name, myLicense.value.firstname)
  : { simple: new Set<string>(), double: new Set<string>() })
// Catégories individuelles que je peux encore ajouter : ouvertes, pas complètes,
// éligibles selon ma licence (âge/genre), pas déjà prises, et compatibles avec la règle
// de cumul en tenant compte de celles déjà prises (ex. junior inscrit en Junior : peut
// encore ajouter Mixte/Féminine) -- mêmes règles que les cases de CategorySelector (cf.
// blockedSimpleCategories).
const myRemainingSimple = computed<string[]>(() => {
  if (!myLicense.value) return []
  const existing = [...myExisting.value.simple]
  const ffdCategory = myLicense.value.category ?? null
  const eligible = eligibleSimpleCategories(ffdCategory, myLicense.value.gender)
  return availableSimpleCategories.value.map(c => c.value).filter(cat =>
    eligible.has(cat)
    && !existing.includes(cat)
    && !fullSimpleCategories.value.includes(cat)
    && !wouldViolateSimpleCumul(existing, cat, ffdCategory)
  )
})

const fillWithMe = (item: CartItem, slotIndex: number) => {
  if (!myLicense.value) return
  item.slots[slotIndex].name = myLicense.value.name.toUpperCase()
  item.slots[slotIndex].firstname = myLicense.value.firstname
  onPick(item, slotIndex, myLicense.value)
  const slot = item.slots[slotIndex]
  if (!item.product.isDouble && !myRemainingSimple.value.includes(slot.category_simple ?? '')) {
    slot.category_simple = myRemainingSimple.value[0] ?? null
  }
}

// Catégorie individuelle suggérée à partir de la vraie catégorie de licence
// (M/F/JG/JF/V, cf. Dartshub/backend _calculate_category) -- reste modifiable ensuite
// (dans la limite de ce que permet blockedSimpleCategories), ce n'est qu'un point
// de départ.
const eventCategoryFromFfd = (ffdCategory: string | null): string => {
  if (ffdCategory === 'F') return 'F'
  if (ffdCategory === 'JG' || ffdCategory === 'JF') return 'J'
  if (ffdCategory === 'V') return 'V'
  return 'M'
}

// Autocomplétion des licenciés, pour tout joueur connecté : périmètre de l'événement
// (comité, à défaut ligue, fédération pour une coupe fédérale) + club du responsable +
// sa propre licence, cf. Dartshub events/routes.py::registration_license_search. Pas
// de recherche sur un emplacement "non licencié" ni sur un tournoi local. Résultats
// rangés par emplacement du panier ; une réponse arrivée après une frappe plus récente
// est ignorée.
const LICENSE_SEARCH_MIN_CHARS = 3
const licenseSuggestions = ref<Record<string, RosterEntry[]>>({})
const latestLicenseQuery: Record<string, string> = {}
const slotKey = (item: CartItem, slotIndex: number) => `${item.id}-${slotIndex}`
const canSearchLicenses = (item: CartItem, slotIndex: number): boolean =>
  !!event.value?.license_search_scope && !!item.product.slotsLicensedHint[slotIndex]
const searchLicensesFor = async (key: string, q: string) => {
  latestLicenseQuery[key] = q
  if (q.trim().length < LICENSE_SEARCH_MIN_CHARS) {
    licenseSuggestions.value[key] = []
    return
  }
  try {
    const { data } = await backendApi.get(`/events/${event.value.id}/registration-license-search`, { params: { q } })
    if (latestLicenseQuery[key] === q) licenseSuggestions.value[key] = data
  } catch {
    if (latestLicenseQuery[key] === q) licenseSuggestions.value[key] = []
  }
}
// Joueur saisi en entier sans cliquer sur la suggestion : sans ça, la question "Licence
// FFD ?" s'affichait et "Licencié(e)" exigeait un numéro -- bloquant pour un joueur dont
// la demande vient d'être transmise au comité (pas encore de numéro, Michel
// Vanhoeserlande, Perno Li Darts, 2026-11-01). Nom ET prénom identiques à un seul
// résultat de la recherche (licences et demandes transmises, cf.
// registration_license_search) : sa licence est choisie d'office, comme un clic sur la
// suggestion. Homonymes : rien, le joueur choisit dans la liste.
const nameKey = (slot: CartSlot) => `${normalize(slot.firstname)}|${normalize(slot.name)}`
// Nom (nameKey) de la licence choisie d'office, par emplacement : si le nom est modifié
// ensuite, ce n'est plus ce joueur -- la sélection est annulée.
const autoPicked: Record<string, string> = {}
const exactLookupTimers: Record<string, ReturnType<typeof setTimeout>> = {}
const autoPickExactLicense = async (item: CartItem, slotIndex: number) => {
  const slot = item.slots[slotIndex]
  const key = nameKey(slot)
  try {
    const { data } = await backendApi.get(`/events/${event.value.id}/registration-license-search`,
      { params: { q: `${slot.firstname.trim()} ${slot.name.trim()}` } })
    const found = (data as RosterEntry[]).filter(r =>
      normalize(r.name) === normalize(slot.name) && normalize(r.firstname) === normalize(slot.firstname))
    // Saisie modifiée ou licence choisie entre-temps : réponse périmée.
    if (found.length !== 1 || slot.licensePicked || nameKey(slot) !== key) return
    autoPicked[slotKey(item, slotIndex)] = key
    onPick(item, slotIndex, found[0])
  } catch {
    // Pas de sélection automatique : la question "Licence FFD ?" reste posée.
  }
}
watch(() => editingItem.value && editingItem.value.slots.map(s => [s.name, s.firstname, s.licensePicked]), () => {
  const item = editingItem.value
  if (!item) return
  item.slots.forEach((slot, slotIndex) => {
    const timerKey = slotKey(item, slotIndex)
    clearTimeout(exactLookupTimers[timerKey])
    if (slot.licensePicked && autoPicked[timerKey] && autoPicked[timerKey] !== nameKey(slot)) {
      slot.licensePicked = false
      slot.licenseCategory = null
      delete autoPicked[timerKey]
    }
    // Pas licenseQuestionShown : une coupe de comité/ligue ne pose pas la question, le
    // joueur doit y être reconnu tout autant.
    if (!canSearchLicenses(item, slotIndex) || slot.licensePicked || !slot.name.trim() || !slot.firstname.trim()) return
    exactLookupTimers[timerKey] = setTimeout(() => autoPickExactLicense(item, slotIndex), 400)
  })
}, { deep: true })

// Périmètre de l'autocomplétion (cf. Dartshub events/routes.py::license_search_scope).
const licenseSearchScopeLabel = computed(() => {
  const scope = event.value?.license_search_scope
  return scope === 'comite' ? `du comité ${event.value.comite}`
    : scope === 'ligue' ? `de la ligue ${event.value.ligue}`
    : 'de la fédération'
})
const licenseSearchHint = computed(() => {
  const where = licenseSearchScopeLabel.value
  const club = authStore.scopes.includes('club_representative') ? ' ou de votre club' : ''
  return `Tapez au moins ${LICENSE_SEARCH_MIN_CHARS} lettres pour rechercher un licencié ${where}${club}, ou saisissez librement un autre joueur.`
})

const onPick = (item: CartItem, slotIndex: number, lic: RosterEntry) => {
  const slot = item.slots[slotIndex]
  slot.licensePicked = true
  slot.licenseAnswer = ''
  slot.otherLigue = false
  slot.licenseCategory = lic.category ?? null
  slot.birth_date = lic.birth_date ? lic.birth_date.slice(0, 10) : null
  slot.gender = lic.gender ?? slot.gender
  if (!item.product.isDouble) {
    // Pas de catégorie suggérée si le joueur ne peut pas la prendre (déjà inscrit...).
    slot.category_simple = null
    const suggested = eventCategoryFromFfd(lic.category ?? null)
    if (!blockedSimpleCategories(item)[suggested]) slot.category_simple = suggested
  }
}

const REASON_ALREADY = 'Déjà inscrit'
const REASON_PARTNER_ALREADY = 'Partenaire déjà inscrit'
const REASON_INELIGIBLE = 'Non éligible'
const REASON_CUMUL = 'Non cumulable'
const REASON_IN_CART = 'Déjà dans le panier'

// Catégories que le joueur prend déjà sur les AUTRES articles du panier (individuel, ou
// double en tant que joueur ou partenaire) : un même joueur peut avoir un article par
// catégorie, et le backend fusionne tout en une seule ligne -- un article Mixte + un
// article Vétéran donnait un vétéran inscrit M,V. Miroir de batch_simple/batch_double
// dans events/routes.py::_validate_category_eligibility.
const cartCategoriesForPlayer = (item: CartItem, name: string, firstname: string) =>
  existingCategoriesForPlayer(
    cartItems.value.filter(other => other.id !== item.id).flatMap((other): { name: string, firstname: string, category_simple?: string | null, category_double?: string | null }[] => other.product.isDouble
      ? other.slots.map(sl => ({ name: sl.name, firstname: sl.firstname, category_double: other.categoryDouble }))
      : [{ name: other.slots[0].name, firstname: other.slots[0].firstname, category_simple: other.slots[0].category_simple }]),
    name, firstname,
  )

// Catégories individuelles grisées pour ce joueur : non éligibles (Vétéran/Féminin/
// Junior selon sa catégorie FFD réelle et son genre -- licence si choisie ci-dessus,
// sinon genre/date de naissance déclarés), qui romprait la règle de cumul (une seule
// catégorie, sauf exception junior : Mixte pour un garçon, Féminine pour une fille), ou
// déjà portée par une ligne d'inscription existante pour cet événement (la reproposer
// n'aurait aucun effet, cf. events/routes.py::_create_inscription_rows, qui
// l'ignorerait). Distinct de fullSimpleCategories (quota événement atteint) : jamais
// affiché "Complet", ce serait faux. Renvoie la raison affichée sur chaque catégorie
// bloquée. Miroir de events/routes.py::_validate_category_eligibility, qui reste la
// vérité.
const slotFfdCategory = (slot: CartSlot): string | null => slot.licensePicked
  ? slot.licenseCategory
  : calculateFfdCategory(slot.gender, slot.birth_date, getCurrentSeason())

const blockedSimpleCategories = (item: CartItem): Record<string, string> => {
  const slot = item.slots[0]
  const registered = [...existingCategoriesForPlayer(allEventInscriptions.value, slot.name, slot.firstname).simple]
  const inCart = [...cartCategoriesForPlayer(item, slot.name, slot.firstname).simple]
  const existing = [...new Set([...registered, ...inCart])]
  const blocked: Record<string, string> = {}
  for (const cat of inCart) blocked[cat] = REASON_IN_CART
  for (const cat of registered) blocked[cat] = REASON_ALREADY
  if (isLocal.value) {
    // Pas de vérification d'âge/genre/licence pour un tournoi local -- seule règle
    // restante : une catégorie individuelle à la fois (pas d'exception junior, qui
    // dépend d'une catégorie FFD qu'on ne connaît plus ici), symétrique du backend.
    for (const cat of SIMPLE_CATEGORIES) {
      if (!blocked[cat.value] && existing.length > 0) blocked[cat.value] = REASON_CUMUL
    }
    return blocked
  }
  const ffdCategory = slotFfdCategory(slot)
  const eligible = eligibleSimpleCategories(ffdCategory, slot.gender)
  // Cumul évalué avec les seules catégories déjà enregistrées ou prises sur un autre
  // article du panier pour ce joueur (sinon un
  // adulte inscrit en Mixte pouvait cocher Féminine). Un conflit avec une catégorie
  // juste cochée ici ne grise rien : la cliquer la remplace (cf. simpleCumulable).
  for (const cat of SIMPLE_CATEGORIES) {
    if (blocked[cat.value]) continue
    if (!eligible.has(cat.value)) {
      blocked[cat.value] = REASON_INELIGIBLE
    } else if (wouldViolateSimpleCumul(existing, cat.value, ffdCategory)) {
      blocked[cat.value] = REASON_CUMUL
    }
  }
  return blocked
}

// Cumul entre catégories cochées dans la modale (cf. CategorySelector::isCumulable) :
// même règle que blockedSimpleCategories, une catégorie non cumulable remplace les autres.
const simpleCumulable = (item: CartItem) => (current: string[], candidate: string): boolean =>
  isLocal.value ? current.length === 0 : !wouldViolateSimpleCumul(current, candidate, slotFfdCategory(item.slots[0]))

// Catégorie double bloquée : non éligible (Double Féminine réservée aux femmes, jamais
// vérifié pour un tournoi local qui ne collecte pas le genre) ou déjà portée par le
// joueur OU le/la partenaire.
const blockedDoubleCategories = (item: CartItem): Record<string, string> => {
  const [player, partner] = item.slots
  const blocked: Record<string, string> = {}
  for (const cat of existingCategoriesForPlayer(allEventInscriptions.value, player.name, player.firstname).double) {
    blocked[cat] = REASON_ALREADY
  }
  // Une seule catégorie double par joueur : déjà en double (joueur ou partenaire) sur
  // un autre article du panier, toutes les catégories doubles sont bloquées.
  const cartHasDouble = item.slots.some(sl => cartCategoriesForPlayer(item, sl.name, sl.firstname).double.size > 0)
  if (cartHasDouble) {
    for (const cat of DOUBLE_CATEGORIES) blocked[cat.value] ??= REASON_IN_CART
  }
  if (partner) {
    for (const cat of existingCategoriesForPlayer(allEventInscriptions.value, partner.name, partner.firstname).double) {
      blocked[cat] ??= REASON_PARTNER_ALREADY
    }
  }
  if (!isLocal.value) {
    for (const cat of ineligibleDoubleCategories(player.gender, partner?.gender)) blocked[cat] ??= REASON_INELIGIBLE
  }
  return blocked
}

// Catégorie cochée mais bloquée (ex. joueur déjà inscrit choisi après avoir coché) :
// l'inscription est refusée tant qu'elle n'est pas décochée.
const blockedSelectionError = (item: CartItem): string | null => {
  const selected = item.product.isDouble
    ? (item.categoryDouble ? [item.categoryDouble] : [])
    : (item.slots[0].category_simple ?? '').split(',').map(c => c.trim()).filter(Boolean)
  const blocked = item.product.isDouble ? blockedDoubleCategories(item) : blockedSimpleCategories(item)
  const categories = item.product.isDouble ? DOUBLE_CATEGORIES : SIMPLE_CATEGORIES
  for (const cat of selected) {
    if (blocked[cat]) return `${getCategoryLabel(cat, categories)} : ${blocked[cat].toLowerCase()}`
  }
  return null
}

const getCartItemPrice = (item: CartItem): number => {
  if (!item.product.isDouble) {
    const slot = item.slots[0]
    const rate = rateCentsFor(slot.licensePicked || slot.otherLigue) / 100
    const count = slot.category_simple ? slot.category_simple.split(',').filter(c => c.trim()).length : 0
    return rate * count
  }
  if (!item.categoryDouble) return 0
  return item.slots.reduce((sum, slot) => sum + doubleShareFor(slot.licensePicked || slot.otherLigue) / 100, 0)
}

const cartItemToPlayer = (item: CartItem): PlayerEntry => {
  if (!item.product.isDouble) {
    const slot = item.slots[0]
    return {
      name: slot.name, firstname: slot.firstname,
      category_simple: slot.category_simple, category_double: null,
      other_ligue: slot.otherLigue,
      license_answer: slot.licenseAnswer,
      license_number: slot.otherLicenseNumber, club_id: slot.otherClubId, club_name: slot.otherClubName,
      partner_name: '', partner_firstname: '', partner_other_ligue: false, partner_gender: null,
      partner_license_answer: '', partner_license_picked: false,
      partner_license_number: '', partner_club_id: null, partner_club_name: '',
      birth_date: slot.birth_date, gender: slot.gender, license_picked: slot.licensePicked,
    }
  }
  const [p1, p2] = item.slots
  return {
    name: p1.name, firstname: p1.firstname,
    category_simple: null, category_double: item.categoryDouble,
    other_ligue: p1.otherLigue,
    license_answer: p1.licenseAnswer,
    license_number: p1.otherLicenseNumber, club_id: p1.otherClubId, club_name: p1.otherClubName,
    partner_name: p2.name, partner_firstname: p2.firstname,
    partner_other_ligue: p2.otherLigue, partner_gender: p2.gender,
    partner_license_answer: p2.licenseAnswer, partner_license_picked: p2.licensePicked,
    partner_license_number: p2.otherLicenseNumber, partner_club_id: p2.otherClubId, partner_club_name: p2.otherClubName,
    birth_date: null, gender: p1.gender, license_picked: p1.licensePicked,
  }
}

const cartItemsToPlayers = (): PlayerEntry[] => cartItems.value.map(cartItemToPlayer)

const event = ref<any>(null)
const loadingEvent = ref(false)
const categoryCounts = ref<Record<string, number>>({})

// Un tournoi local n'a ni tarif licencié/non licencié distinct, ni vérification de
// licence, ni catégories toutes ouvertes par défaut -- cf. events/routes.py (backend)
// pour le même branchement sur event.event_type côté serveur.
const isLocal = computed(() => event.value?.event_type === 'local')
// Événement de démonstration : mêmes règles, mais register_and_pay enregistre directement
// sans HelloAsso (cf. Dartshub events/routes.py::create_demo_event).
const isDemo = computed(() => event.value?.event_type === 'demo')
// Coupe de ligue/comité : fermée aux licenciés des autres ligues, la case "Licencié dans
// une autre ligue" n'a donc pas lieu d'être (refusée côté serveur de toute façon).
const closedToOtherLigues = computed(() => ['ligue', 'comite'].includes(event.value?.cup_scope))
// Question "Licence FFD ?" posée pour un joueur saisi (nom et prénom) sans licence
// choisie dans les suggestions -- jamais sur un tournoi local (aucune licence vérifiée)
// ni sur une coupe de comité/ligue (fermée aux joueurs hors des suggestions).
const licenseQuestionAsked = (name: string, firstname: string, licensePicked: boolean): boolean =>
  !isLocal.value && !closedToOtherLigues.value && !licensePicked && !!name.trim() && !!firstname.trim()
const licenseQuestionShown = (slot: CartSlot): boolean =>
  licenseQuestionAsked(slot.name, slot.firstname, slot.licensePicked)
const setLicenseAnswer = (slot: CartSlot, answer: 'yes' | 'no') => {
  slot.licenseAnswer = answer
  slot.otherLigue = answer === 'yes'
}
const currentSeason = getCurrentSeason()
// Champs "licencié saisi" envoyés au serveur (cf. Dartshub PublicRegistrationItem, où
// other_ligue = réponse "oui"), vides sinon.
const otherLigueFields = (otherLigue: boolean, number: string, clubId: number | null, clubName: string) =>
  otherLigue
    ? { license_number: number ? parseInt(number, 10) : null, club_id: clubId, club_name: clubId ? null : clubName || null }
    : { license_number: null, club_id: null, club_name: null }
const partnerOtherLigueFields = (p: PlayerEntry) => {
  const f = otherLigueFields(!!p.category_double && p.partner_other_ligue, p.partner_license_number, p.partner_club_id, p.partner_club_name)
  return { partner_license_number: f.license_number, partner_club_id: f.club_id, partner_club_name: f.club_name }
}

// Tarif réellement configuré sur l'événement (event.price_cents /
// price_cents_unlicensed) -- plus de barème fixe 6€/8€ codé en dur ici.
const licensedRateCents = computed(() => event.value?.price_cents ?? 0)
const unlicensedRateCents = computed(() => event.value?.price_cents_unlicensed ?? licensedRateCents.value)
const formatEuros = (cents: number) => `${(cents / 100).toFixed(2)} €`
// Tarif unique pour un tournoi local, quel que soit le statut licencié déclaré (absent
// de toute façon) -- symétrique de _rate_cents_for côté backend.
const rateCentsFor = (isLicensed: boolean | null | undefined): number =>
  (isLocal.value || isLicensed ? licensedRateCents.value : unlicensedRateCents.value)
// Prix par équipe des catégories doubles (null hors mode équipe) et part d'un joueur pour
// une catégorie double -- cf. eventDisplay.ts::doubleShareCents.
const eventTeamPrices = computed(() => teamPrices(event.value))
const doubleShareFor = (isLicensed: boolean | null | undefined): number =>
  doubleShareCents(event.value, isLicensed, rateCentsFor(isLicensed))
// Prix d'une ligne (catégories individuelles + doubles) pour un joueur, en euros.
const categoriesPrice = (isLicensed: boolean | null | undefined, simple: string | null, double: string | null): number => {
  const count = (cats: string | null) => (cats ? cats.split(',').filter(c => c.trim()).length : 0)
  return (rateCentsFor(isLicensed) * count(simple) + doubleShareFor(isLicensed) * count(double)) / 100
}

// Catégories proposées à l'inscription : toutes par défaut (officiel), ou seulement
// celles choisies par l'organisateur d'un tournoi local (event.open_categories, null =
// toutes). Les listes complètes (SIMPLE_CATEGORIES/DOUBLE_CATEGORIES) restent utilisées
// ailleurs pour l'affichage (libellés d'inscriptions existantes sur une catégorie
// désormais fermée).
const openCategoriesSet = computed<Set<string> | null>(() => {
  const oc = event.value?.open_categories
  return Array.isArray(oc) ? new Set<string>(oc) : null
})
const availableSimpleCategories = computed(() =>
  openCategoriesSet.value ? SIMPLE_CATEGORIES.filter(c => openCategoriesSet.value!.has(c.value)) : SIMPLE_CATEGORIES
)
const availableDoubleCategories = computed(() =>
  openCategoriesSet.value ? DOUBLE_CATEGORIES.filter(c => openCategoriesSet.value!.has(c.value)) : DOUBLE_CATEGORIES
)
// Produits proposés au catalogue : un produit individuel n'a de sens que s'il reste au
// moins une catégorie individuelle ouverte (idem doublette), sinon le bouton mènerait à
// un sélecteur de catégorie vide.
const visibleProducts = computed<ProductDef[]>(() =>
  (isLocal.value ? PRODUCTS_LOCAL : PRODUCTS).filter(p =>
    p.isDouble ? availableDoubleCategories.value.length > 0 : availableSimpleCategories.value.length > 0
  )
)

const fullSimpleCategories = computed(() => {
  const limits: Record<string, number> = event.value?.category_limits ?? {}
  return SIMPLE_CATEGORIES.map(c => c.value).filter(v => {
    const limit = limits[v]
    return limit != null && (categoryCounts.value[v] ?? 0) >= limit
  })
})

const fullDoubleCategories = computed(() => {
  const limits: Record<string, number> = event.value?.category_limits ?? {}
  return DOUBLE_CATEGORIES.map(c => c.value).filter(v => {
    const limit = limits[v]
    return limit != null && (categoryCounts.value[v] ?? 0) >= limit
  })
})
const players = ref<PlayerEntry[]>([])
const step = ref<'form' | 'review' | 'payment' | 'confirmed'>('form')
const reviewData = ref<any>(null)
const submitting = ref(false)
const successMessage = ref('')
const alreadyExisted = ref<string[]>([])
const generalError = ref('')
const nonLicensedNames = ref<string[]>([])
const myEventInscriptions = ref<any[]>([])
const myInscriptions = ref<any[]>([])
const alreadyExistedSet = ref<Set<string>>(new Set())
// Part du reste à régler sur place (inscriptions "onsite" pas encore encaissées).
const onsiteDue = ref(0)
const submittingMode = ref<'online' | 'onsite' | null>(null)
const canSubmitRegistration = computed(() =>
  !submitting.value && reviewData.value?.can_submit !== false && !(nonLicensedNames.value.length > 0 && LICENSED_ONLY.value)
)

const goBackToForm = () => {
  step.value = 'form'
}

const isSimpleCatSelected = (player: { category_simple: string | null }, catValue: string): boolean =>
  !!player.category_simple && player.category_simple.split(',').map(c => c.trim()).includes(catValue)

const totalPrice = computed(() =>
  cartItems.value.reduce((sum, item) => sum + getCartItemPrice(item), 0)
)

const nonLicensedNamesDisplay = computed(() => nonLicensedNames.value.join(', '))

const mergedMyInscriptions = computed(() => {
  const fromPayments = myInscriptions.value.map(ins => ({
    id: ins.inscription_id,
    firstname: ins.firstname || '',
    name: ins.name || '',
    club: ins.club || null,
    category_simple: ins.category_simple ?? null,
    category_double: ins.category_double ?? null,
    doublette_id: ins.doublette_id ?? null,
  }))

  const knownIds = new Set(fromPayments.map(i => i.id))

  // Inscriptions visibles dans my-registrations mais absentes de my-event-inscriptions
  // (ex : inscrites par un autre utilisateur au nom du joueur)
  const extra = myEventInscriptions.value
    .filter(ins => !knownIds.has(ins.id))
    .map(ins => ({
      id: ins.id,
      firstname: ins.firstname || '',
      name: ins.name || '',
      club: ins.club || null,
      category_simple: ins.category_simple ?? null,
      category_double: ins.category_double ?? null,
      doublette_id: ins.doublette ?? null,
    }))

  return [...fromPayments, ...extra]
})

// Les inscriptions du joueur connecté d'abord, puis celles qu'il a faites pour d'autres.
const inscriptionSections = computed(() => {
  const me = `${normalize(authStore.name ?? '')} ${normalize(authStore.firstname ?? '')}`
  const isMe = (ins: { name: string, firstname: string }) => `${normalize(ins.name)} ${normalize(ins.firstname)}` === me
  const own = mergedMyInscriptions.value.filter(isMe)
  const others = mergedMyInscriptions.value.filter(ins => !isMe(ins))
  return [
    { title: 'Mes inscriptions pour cet événement', rows: own },
    { title: 'Joueurs que vous avez inscrits', rows: others },
  ].filter(section => section.rows.length)
})

const refreshMyInscriptions = async () => {
  const eventIdNum = parseInt(route.params.eventId as string)
  if (!authStore.isAuthenticated || isNaN(eventIdNum)) return

  try {
    const { data: insData } = await backendApi.get('/inscriptions/my-registrations', {
      headers: { Authorization: `Bearer ${authStore.token}` },
    })
    myEventInscriptions.value = insData.filter((ins: any) => ins.event_id === eventIdNum)

    const { data: payData } = await backendApi.get(
      `/events/my-event-inscriptions/${eventIdNum}`,
      { headers: { Authorization: `Bearer ${authStore.token}` } }
    )

    myInscriptions.value = payData.inscriptions || []

    // La liste complète des inscrits sert à retrouver le nom du/de la partenaire
    // (getPartnerNameById) et à griser les catégories déjà prises : chargée au montage,
    // elle ne connaissait pas encore les lignes créées par le paiement qui vient d'être
    // confirmé -- d'où un "cherche partenaire" à tort sur une doublette bien liée.
    const [insAllRes, countsRes] = await Promise.all([
      backendApi.get('/inscriptions/active', { params: { event_id: eventIdNum } }).catch(() => ({ data: allEventInscriptions.value })),
      backendApi.get('/inscriptions/category-counts', { params: { event_id: eventIdNum } }).catch(() => ({ data: categoryCounts.value })),
    ])
    allEventInscriptions.value = insAllRes.data
    categoryCounts.value = countsRes.data

    onsiteDue.value = (payData.summary?.onsite_due_cents || 0) / 100

  } catch (err) {
    console.error("Erreur lors du chargement des inscriptions/paiements", err)
    toast.error("Impossible de charger vos inscriptions ou statut de paiement")
    onsiteDue.value = 0
  }
}

const getCategoryLabel = (value: string, cats: typeof SIMPLE_CATEGORIES) =>
  cats.find(c => c.value === value.trim())?.label || value.trim()

const splitCats = (categories: string | null): string[] =>
  (categories || '').split(',').map(c => c.trim()).filter(Boolean)

const allEventInscriptions = ref<any[]>([])

const getPartnerNameById = (id: number | null): string | null => {
  if (!id) return null
  // Repli sur mes propres inscriptions (le/la partenaire y est souvent, inscrit·e par
  // le même compte) si la liste complète n'est pas encore à jour.
  const p = allEventInscriptions.value.find(i => i.id === id)
    ?? myInscriptions.value.find(i => i.inscription_id === id)
    ?? myEventInscriptions.value.find(i => i.id === id)
  return p ? `${p.name} ${p.firstname}` : null
}

// Premier champ manquant d'une inscription, ou null si elle est complète -- partagé par la
// modale de saisie ("Ajouter au panier") et la vérification finale du panier.
const playerError = (p: PlayerEntry): string | null => {
  if (!p.name.trim() || !p.firstname.trim()) return 'nom et prénom requis'
  if (!p.category_simple && !p.category_double) return 'au moins une catégorie requise'
  // Genre exigé pour Féminin, et pour Masculin hors tournoi local (fermé aux joueuses,
  // cf. eligibleSimpleCategories) ; pour Vétéran/Junior un non-licencié sans genre voit
  // déjà ces catégories grisées (cf. blockedSimpleCategories), un licencié a le sien via
  // sa licence.
  if (isSimpleCatSelected(p, 'F') && !p.gender && !p.license_picked) return 'genre requis pour la catégorie Féminin'
  if (!isLocal.value && isSimpleCatSelected(p, 'M') && !p.gender && !p.license_picked) return 'genre requis pour la catégorie Masculin'
  // La date de naissance ne sert qu'à vérifier l'éligibilité Vétéran/Junior d'un
  // joueur non licencié (cf. _calculate_category côté backend) -- inutile de la
  // demander pour Mixte/Féminin, où seul le genre compte.
  if ((isSimpleCatSelected(p, 'V') || isSimpleCatSelected(p, 'J')) && !p.birth_date && !p.license_picked) {
    return "date de naissance requise pour vérifier l'éligibilité Vétéran/Junior"
  }
  if (p.category_double && (!p.partner_name.trim() || !p.partner_firstname.trim())) {
    return 'nom du partenaire obligatoire pour la doublette'
  }
  if (p.category_double && p.partner_name.trim().toLowerCase() === p.name.trim().toLowerCase()
    && p.partner_firstname.trim().toLowerCase() === p.firstname.trim().toLowerCase()) {
    return 'le joueur et son partenaire doivent être deux personnes différentes'
  }
  if (p.category_double === 'DF' && (!p.gender || !p.partner_gender)) {
    return 'genre du joueur et du partenaire requis pour la Double Féminine'
  }
  const declarationError = (who: string, answer: string, number: string, clubName: string) => {
    if (!answer) return `${who} : indiquez s'il s'agit d'un(e) licencié(e) FFD ou non`
    if (answer !== 'yes') return null
    if (number.length < 6) return `${who} : numéro de licence requis`
    if (!clubName) return `${who} : club à valider`
    return null
  }
  if (licenseQuestionAsked(p.name, p.firstname, p.license_picked)) {
    const err = declarationError(`${p.firstname} ${p.name}`.trim(), p.license_answer, p.license_number, p.club_name)
    if (err) return err
  }
  if (p.category_double && licenseQuestionAsked(p.partner_name, p.partner_firstname, p.partner_license_picked)) {
    const err = declarationError(`${p.partner_firstname} ${p.partner_name}`.trim(), p.partner_license_answer,
      p.partner_license_number, p.partner_club_name)
    if (err) return err
  }
  return null
}

const validateRegistration = async () => {
  if (!cartItems.value.length) {
    toast.error('Ajoutez au moins une inscription au panier')
    return
  }

  players.value = cartItemsToPlayers()
  for (const [i, item] of cartItems.value.entries()) {
    const error = cartItemError(item)
    if (error) {
      toast.error(`Ligne ${i + 1} : ${error}`)
      openItemEditor(i)
      return
    }
  }

  submitting.value = true
  generalError.value = ''
  nonLicensedNames.value = []

  const eventId = parseInt(route.params.eventId as string)

  try {
    const payload = {
      event_id: eventId,
      registrations: players.value.map(p => ({
        name: p.name.trim().toUpperCase(),
        firstname: normalizeFirstname(p.firstname),
        category_simple: p.category_simple || null,
        category_double: p.category_double || null,
        other_ligue: p.other_ligue,
        ...otherLigueFields(p.other_ligue, p.license_number, p.club_id, p.club_name),
        partner_name: p.category_double ? p.partner_name.trim().toUpperCase() || null : null,
        partner_firstname: p.category_double ? normalizeFirstname(p.partner_firstname) || null : null,
        partner_other_ligue: p.category_double ? p.partner_other_ligue : false,
        ...partnerOtherLigueFields(p),
        partner_gender: p.category_double ? p.partner_gender || null : null,
        birth_date: p.birth_date || null,
        gender: p.gender || null,
      }))
    }

    const { data } = await backendApi.post('/inscriptions/public-register/validate', payload)

    if (data.already_existed?.length) {
      alreadyExistedSet.value = new Set(
        (data.already_existed as string[]).map((s: string) => s.toLowerCase())
      )

      players.value = players.value.filter(
        p => !alreadyExistedSet.value.has(`${p.firstname.trim()} ${p.name.trim()}`.toLowerCase())
      )

      toast.warning(`Déjà inscrit(s) : ${data.already_existed.join(', ')} (non comptabilisé(s)).`)
    } else {
      alreadyExistedSet.value = new Set()
    }

    if (!data.results?.length) {
      generalError.value = data.message || 'Aucun résultat reçu du serveur'
      toast.error(generalError.value)
      return
    }

    const nonLicensed = (data.results ?? []).filter((r: any) =>
      !isResultAlreadyExisted(r.name, r.firstname) && r.is_licensed === false
    )
    if (nonLicensed.length > 0) {
      nonLicensedNames.value = nonLicensed.map((r: any) => `${r.firstname} ${r.name}`)
    } else {
      nonLicensedNames.value = []
    }

    reviewData.value = data
    step.value = 'review'

    if (LICENSED_ONLY.value && nonLicensed.length > 0) {
      return
    }

    if (data.license_not_found?.length > 0) {
      toast.warning(`Certaines licences n'ont pas été trouvées : tarif non licencié appliqué (${formatEuros(unlicensedRateCents.value)})`)
    }
  } catch (err: any) {
    generalError.value = err.response?.data?.detail || err.response?.data?.message || 'Erreur lors de la vérification'
    toast.error(generalError.value)
  } finally {
    submitting.value = false
  }
}

// En ligne, inscription et paiement ne font qu'une étape : aucune ligne n'est créée
// avant que HelloAsso confirme le paiement (cf. events/routes.py::register_and_pay). On
// redirige directement vers HelloAsso ; la confirmation se fait au retour, dans
// onMounted, via /events/verify_helloasso_payment. Sur place, l'inscription est
// enregistrée tout de suite, paiement en attente jusqu'à l'encaissement le jour J.
const payAndRegister = async (mode: 'online' | 'onsite') => {
  submitting.value = true
  submittingMode.value = mode
  generalError.value = ''

  const eventId = parseInt(route.params.eventId as string)

  try {
    const results = reviewData.value?.results ?? []

    const registrations = players.value.map((p, i) => {
      const result = results.find((r: any) => r.index === i)
      const name = result?.is_licensed ? result.name : p.name.trim()
      const firstname = result?.is_licensed ? result.firstname : p.firstname.trim()
      const partnerName = p.category_double
        ? (result?.partner?.is_licensed ? result.partner.name : p.partner_name.trim()) || null
        : null
      const partnerFirstname = p.category_double
        ? (result?.partner?.is_licensed ? result.partner.firstname : p.partner_firstname.trim()) || null
        : null
      return {
        name,
        firstname,
        is_licensed: result?.is_licensed ?? false,
        other_ligue: p.other_ligue,
        ...otherLigueFields(p.other_ligue, p.license_number, p.club_id, p.club_name),
        category_simple: p.category_simple || null,
        category_double: p.category_double || null,
        partner_name: partnerName,
        partner_firstname: partnerFirstname,
        partner_is_licensed: result?.partner?.is_licensed ?? false,
        partner_other_ligue: p.partner_other_ligue,
        ...partnerOtherLigueFields(p),
        partner_gender: p.partner_gender || null,
        birth_date: p.birth_date || null,
        gender: p.gender || null,
      }
    })

    if (!registrations.length) {
      toast.info('Tous les joueurs sont déjà inscrits.')
      submitting.value = false
      return
    }

    const baseUrl = `${window.location.origin}/inscription/${eventId}`
    const { data } = await backendApi.post('/events/register_and_pay', {
      event_id: eventId,
      registrations,
      payment_mode: mode,
      success_url: baseUrl,
      cancel_url: baseUrl,
    })

    // Démonstration ou paiement sur place : inscription déjà enregistrée côté serveur,
    // pas de HelloAsso.
    if (data.demo || data.onsite) {
      toast.success(data.demo ? 'Inscription enregistrée (démonstration, aucun paiement).' : 'Inscription enregistrée !')
      await refreshMyInscriptions()
      step.value = 'confirmed'
      clearPersistedCart()
      submitting.value = false
      return
    }

    sessionStorage.setItem('helloasso_intent_id', data.checkout_intent_id)
    sessionStorage.setItem('helloasso_event_id', String(eventId))
    window.location.href = data.url
  } catch (err: any) {
    generalError.value = err.response?.data?.detail
      || (mode === 'onsite' ? "Erreur lors de l'enregistrement de l'inscription" : 'Erreur lors de la création du paiement')
    toast.error(generalError.value)
    submitting.value = false
    submittingMode.value = null
  }
}

const getPartnerPrice = (partner: any): number => doubleShareFor(partner.is_licensed) / 100

const isResultAlreadyExisted = (name: string, firstname: string) =>
  alreadyExistedSet.value.has(`${firstname.trim()} ${name.trim()}`.toLowerCase())

// Somme exactement ce qui est affiché ligne par ligne dans le récap (une ligne par
// déclarant + une par partenaire), sans déduplication par nom -- un même nom peut
// légitimement apparaître plusieurs fois dans le même lot (ex. inscrit en individuel
// ET en doublette), ce n'est pas une inscription en double. L'ancienne version
// dédupliquait par (nom, prénom) sur tout le lot et sautait alors des lignes entières
// dès qu'un nom se répétait, faisant baisser le total à tort.
const calculateGlobalTotal = computed(() => {
  if (!reviewData.value?.results) return 0
  let total = 0
  for (const r of reviewData.value.results) {
    if (!isResultAlreadyExisted(r.name, r.firstname)) {
      total += getOwnPrice(r)
    }
    if (r.partner && !isResultAlreadyExisted(r.partner.name, r.partner.firstname)) {
      total += getPartnerPrice(r.partner)
    }
  }
  return total
})

// Récap regroupé par joueur : le backend renvoie une ligne par inscription (individuel,
// doublette + partenaire), un même joueur y apparaît donc plusieurs fois. Même total
// que calculateGlobalTotal, juste réparti par personne.
interface SummaryPlayer {
  key: string
  name: string
  firstname: string
  is_licensed: boolean
  club: string | null
  alreadyExisted: boolean
  entries: { label: string, partner: string | null, price: number }[]
  warnings: string[]
  total: number
}

const summaryPlayers = computed<SummaryPlayer[]>(() => {
  const byKey = new Map<string, SummaryPlayer>()
  const playerFor = (p: any): SummaryPlayer => {
    const key = `${p.firstname.trim()} ${p.name.trim()}`.toLowerCase()
    let player = byKey.get(key)
    if (!player) {
      player = {
        key, name: p.name, firstname: p.firstname, is_licensed: !!p.is_licensed, club: p.club ?? null,
        alreadyExisted: isResultAlreadyExisted(p.name, p.firstname), entries: [], warnings: [], total: 0,
      }
      byKey.set(key, player)
    }
    return player
  }
  for (const r of (reviewData.value?.results ?? []).filter((r: any) => r.index !== null)) {
    const own = playerFor(r)
    const ownPrice = getOwnPrice(r)
    own.entries.push({
      label: r.category_simple ? getCategoryLabel(r.category_simple, SIMPLE_CATEGORIES)
        : getCategoryLabel(r.category_double, DOUBLE_CATEGORIES),
      partner: r.partner ? `${r.partner.firstname} ${r.partner.name}` : null,
      price: ownPrice,
    })
    if (!own.alreadyExisted) own.total += ownPrice
    if (r.warning) own.warnings.push(r.warning)
    if (r.partner) {
      const partner = playerFor(r.partner)
      const partnerPrice = getPartnerPrice(r.partner)
      partner.entries.push({
        label: getCategoryLabel(r.category_double, DOUBLE_CATEGORIES),
        partner: `${r.firstname} ${r.name}`,
        price: partnerPrice,
      })
      if (!partner.alreadyExisted) partner.total += partnerPrice
    }
  }
  return [...byKey.values()]
})

// Part du déclarant seul, sans son partenaire -- affichée sur sa propre ligne dans le
// récap.
const getOwnPrice = (result: any): number =>
  categoriesPrice(result.is_licensed, result.category_simple, result.category_double)

onMounted(async () => {
  const eventId = parseInt(route.params.eventId as string)
  if (isNaN(eventId)) return

  loadingEvent.value = true
  try {
    const [{ data }, countsRes, insRes] = await Promise.all([
      backendApi.get(`/events/${eventId}`),
      backendApi.get('/inscriptions/category-counts', { params: { event_id: eventId } }).catch(() => ({ data: {} })),
      backendApi.get('/inscriptions/active', { params: { event_id: eventId } }).catch(() => ({ data: [] })),
    ])
    allEventInscriptions.value = insRes.data
    event.value = data
    categoryCounts.value = countsRes.data
  } catch {
    event.value = null
  } finally {
    loadingEvent.value = false
  }

  if (!isLocal.value && authStore.isAuthenticated) {
    // La licence retenue est celle de la saison courante : il faut la vraie (/seasons),
    // pas le repli local, cf. useSeasonStore.
    await useSeasonStore().load()
    await loadMyLicense()
  }

  if (authStore.isAuthenticated) {
    await verifyPendingPayment(eventId)
    await refreshMyInscriptions()
  }
})

// Retour de HelloAsso déconnecté : lien ouvert depuis un réseau social (navigateur
// intégré de l'appli), le paiement validé dans l'appli bancaire et le retour ouvert
// dans le navigateur par défaut, sans session ni sessionStorage. Seuls les paramètres
// de l'URL de retour survivent : on garde la vérification en attente jusqu'à la
// connexion, sinon le joueur, invité à se connecter, recommençait toute l'inscription.
const pendingPaymentReturn = computed(() =>
  !!route.query.checkoutIntentId && route.query.code === 'succeeded')

watch(() => authStore.isAuthenticated, async (isAuth) => {
  const eventId = parseInt(route.params.eventId as string)
  if (!isAuth || isNaN(eventId)) return
  if (!isLocal.value) {
    await useSeasonStore().load()
    await loadMyLicense()
  }
  await verifyPendingPayment(eventId)
  await refreshMyInscriptions()
})

const verifyPendingPayment = async (eventId: number) => {
  const queryIntentId = route.query.checkoutIntentId as string | undefined
  const queryCode = route.query.code as string | undefined
  const sessionIntentId = sessionStorage.getItem('helloasso_intent_id')
  const storedEventId = sessionStorage.getItem('helloasso_event_id')

  const intentId = queryIntentId || (storedEventId === String(eventId) ? sessionIntentId : null)
  const shouldVerify = (queryIntentId && queryCode === 'succeeded') || (sessionIntentId && storedEventId === String(eventId))
  if (!intentId || !shouldVerify) return

  sessionStorage.removeItem('helloasso_intent_id')
  sessionStorage.removeItem('helloasso_event_id')
  try {
    // Le backend répond 200 avec { paid: false } (pas une erreur HTTP) quand le
    // paiement n'a pas abouti (retour en arrière depuis HelloAsso sans payer,
    // paiement refusé...) -- il ne suffit pas que la requête n'ait pas levé
    // d'exception pour confirmer l'inscription, il faut lire paid explicitement.
    const { data } = await backendApi.post('/events/verify_helloasso_payment', {
      checkout_intent_id: intentId,
      event_id: eventId,
    }, { headers: { Authorization: `Bearer ${authStore.token}` } })
    if (data?.paid) {
      toast.success('Paiement effectué, inscription confirmée !')
      await refreshMyInscriptions()
      step.value = 'confirmed'
      clearPersistedCart()
    } else {
      toast.info('Paiement non finalisé. Vous pouvez réessayer.')
    }
  } catch { }
}
</script>

<style scoped>
.payment-step {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.payment-cta {
  background: var(--color-accent-bg, #e8f4fd);
  border: 2px solid var(--color-accent, #1a73e8);
  border-radius: var(--radius, 8px);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8rem;
  text-align: center;
}

.payment-cta h4 {
  margin: 0;
  font-size: 1.1rem;
}

.payment-hint {
  margin: 0;
  font-size: 0.95rem;
  color: var(--color-text-muted, #555);
}

.payment-amount {
  font-size: 1.1rem;
}

.redirecting {
  font-style: italic;
  color: var(--color-text-muted, #555);
}

.on-site-option {
  text-align: center;
}

.btn-link {
  background: none;
  border: none;
  color: var(--color-text-muted, #777);
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0;
}

.btn-link:hover {
  color: var(--color-text, #333);
}

.closed-banner {
  background: color-mix(in srgb, var(--color-error, #e74c3c) 15%, var(--color-bg-lighter, #fff));
  border: 1px solid var(--color-error, #e74c3c);
  border-radius: var(--radius, 8px);
  padding: 1.2rem 1.5rem;
  margin-bottom: 1.2rem;
  color: var(--color-error, #c0392b);
}

.closed-banner strong {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 1.1rem;
}

.closed-banner p {
  margin: 0;
  font-size: 0.9rem;
}

.deadline-hint {
  margin: 0 0 1rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-accent);
}

.demo-banner {
  background: color-mix(in srgb, #7e57c2 12%, var(--color-bg-lighter, #fff));
  border: 1px dashed #7e57c2;
  border-radius: var(--radius, 8px);
  padding: 0.9rem 1.2rem;
  margin-bottom: 1.2rem;
}

.demo-banner strong {
  display: block;
  margin-bottom: 0.3rem;
  color: #7e57c2;
}

.demo-banner p {
  margin: 0;
  font-size: 0.9rem;
}

.cart-list {
  margin-bottom: 1rem;
}

.cart-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 0.7rem 0.9rem;
  margin-bottom: 0.5rem;
  border: 1px solid var(--color-border, #ddd);
  border-radius: var(--radius, 8px);
  background: var(--color-bg-lighter, #fff);
}

.cart-line-info {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  cursor: pointer;
  min-width: 0;
}

.cart-line-title {
  font-weight: 600;
}

.cart-line-detail {
  font-size: 0.85rem;
  opacity: 0.8;
}

.cart-line-warning {
  font-size: 0.8rem;
  color: var(--color-accent, #e65100);
}

.cart-line-actions {
  display: flex;
  gap: 0.4rem;
}

/* Pas de défilement DANS la modale (la liste de suggestions du PlayerPicker y serait
coupée) : elle prend toute sa hauteur, et c'est le fond qui défile si elle dépasse. */
.item-modal-overlay {
  align-items: flex-start;
  overflow-y: auto;
  padding: 2rem 0;
}

.item-modal {
  max-width: 640px;
  max-height: none;
  overflow: visible;
  text-align: left;
}

.item-modal-title {
  margin: 0 0 0.8rem;
}

.item-modal-actions {
  justify-content: flex-end;
}

.cart-bar {
  position: sticky;
  bottom: 0;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 1rem;
  padding: 0.7rem 0.9rem;
  border: 1px solid var(--color-border, #ddd);
  border-radius: var(--radius, 8px);
  background: var(--color-bg, #fff);
  box-shadow: 0 -4px 14px rgba(0, 0, 0, 0.12);
}

.cart-bar-summary {
  background: none;
  border: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
  padding: 0;
}

.its-me-btn {
  margin-bottom: 0.4rem;
  padding: 0.3rem 0.7rem;
  font-size: 0.85rem;
}

/* TEMPORAIRE */
.licensed-only-banner {
  background: color-mix(in srgb, var(--color-accent, #f9a825) 15%, var(--color-bg-lighter, #fff));
  border: 1px solid var(--color-accent, #f9a825);
  border-radius: var(--radius, 8px);
  padding: 0.9rem 1.2rem;
  margin-bottom: 1.2rem;
  color: var(--color-fg, #795548);
}

.licensed-only-banner strong {
  display: block;
  margin-bottom: 0.3rem;
  color: var(--color-accent, #e65100);
}

.licensed-only-banner p {
  margin: 0;
  font-size: 0.9rem;
}

.warning {
  color: var(--color-accent, #e65100);
  background: color-mix(in srgb, var(--color-accent, #f9a825) 15%, var(--color-bg-lighter, #fff));
  padding: 0.5rem;
  border-radius: 6px;
}

.event-banner {
  background: var(--color-accent-bis, #1a3a5c);
  color: var(--color-bg, #fff);
  padding: 1.2rem 1.5rem;
  border-radius: var(--radius, 8px);
  margin-bottom: 1.5rem;
}

.event-banner h3 {
  margin: 0 0 0.5rem;
  font-size: 1.3rem;
}

.event-banner-link {
  display: inline-block;
  margin-top: 0.5rem;
  color: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: underline;
}

.event-banner p {
  margin: 0.25rem 0;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.pricing-info {
  background: var(--color-bg, #fff);
  border: 1px solid var(--color-light-shadow, #ddd);
  border-radius: var(--radius, 8px);
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
}

.pricing-info h4 {
  margin: 0 0 0.75rem;
  color: var(--color-accent, #1a3a5c);
}

.pricing-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.5rem;
}

.payment-modes-hint {
  margin: 0.75rem 0 0;
  font-size: 0.9rem;
  font-weight: 600;
}

.pricing-item {
  display: flex;
  justify-content: space-between;
  padding: 0.4rem 0.6rem;
  background: var(--color-light-shadow, #f5f5f5);
  border-radius: 4px;
  font-size: 0.9rem;
}

.pricing-item strong {
  color: var(--color-accent, #1a3a5c);
}

.catalog {
  margin-bottom: 1.5rem;
}

.catalog-title {
  margin: 0 0 0.6rem;
  color: var(--color-accent, #1a3a5c);
}

.catalog-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
}

.catalog-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.3rem;
  padding: 0.9rem 1rem;
  border: 2px dashed var(--color-light-shadow, #ccc);
  border-radius: var(--radius, 8px);
  background: var(--color-bg, #fff);
  color: var(--color-fg, #333);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: border-color 0.15s;
}

.catalog-btn:hover {
  border-color: var(--color-accent, #1a3a5c);
  border-style: solid;
}

.catalog-icon {
  font-size: 1.3rem;
}

.catalog-label {
  font-weight: 600;
  font-size: 0.9rem;
}

.catalog-add {
  font-size: 0.78rem;
  color: var(--color-accent, #1a3a5c);
  font-weight: 600;
}

.cart-slot {
  margin-bottom: 0.6rem;
}

.slot-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--color-fg-darker, #888);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 0.2rem;
}

.license-question {
  margin-top: 0.5rem;
  padding: 0.6rem 0.7rem;
  border-left: 3px solid var(--color-accent);
  background: var(--color-bg-lighter, transparent);
  border-radius: var(--radius, 8px);
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.license-question-title {
  margin: 0 0 0.2rem;
  font-size: 0.9rem;
  font-weight: 600;
}

.license-question-option {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  cursor: pointer;
}

.license-question-option input {
  cursor: pointer;
}

.player-entry {
  border: 1px solid var(--color-light-shadow, #ddd);
  border-radius: var(--radius, 8px);
  padding: 1.2rem;
  margin-bottom: 1.2rem;
  background: var(--color-bg, #fff);
}

.player-entry-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.player-entry-header h4 {
  margin: 0;
  color: var(--color-accent, #1a3a5c);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-group {
  margin-bottom: 0.9rem;
}

.form-group label {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
  color: var(--color-fg, #333);
}

/* Le 2e sélecteur l'emporte sur la règle globale input:not([type="checkbox"]):not([type="radio"])
(styles.css), plus spécifique que .form-input seul : sans lui, les champs texte/date
prenaient le padding et la bordure globaux, différents des <select> voisins. :deep pour
que les champs des composants enfants (PlayerPicker, OtherLigueClub) suivent aussi. */
:deep(.form-input),
:deep(input.form-input:not([type="checkbox"]):not([type="radio"])) {
  display: block;
  width: 100%;
  min-height: 2.5rem;
  padding: 0.5rem 0.7rem;
  border: 1px solid var(--color-light-shadow, #ccc);
  border-radius: var(--radius, 6px);
  font-size: 0.95rem;
  background: var(--color-bg, #fff);
  color: var(--color-fg, #333);
  box-sizing: border-box;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.9rem;
  cursor: pointer;
}

.doublette-group {
  background: var(--color-light-shadow, #f5f5f5);
  padding: 0.8rem;
  border-radius: var(--radius, 6px);
}

.player-price {
  margin-top: 0.5rem;
  font-size: 0.95rem;
  color: var(--color-accent, #1a3a5c);
  text-align: right;
}

.btn-remove {
  background: transparent;
  border: 1px solid var(--color-error, #e74c3c);
  color: var(--color-error, #e74c3c);
  padding: 0.3rem 0.8rem;
  border-radius: var(--radius, 6px);
  cursor: pointer;
  font-size: 0.85rem;
}

.btn-add-player {
  width: 100%;
  padding: 0.7rem;
  border: 2px dashed var(--color-light-shadow, #ccc);
  background: transparent;
  border-radius: var(--radius, 8px);
  cursor: pointer;
  font-size: 1rem;
  color: var(--color-accent, #1a3a5c);
  margin-bottom: 1.2rem;
  transition: border-color 0.2s;
}

.btn-add-player:hover {
  border-color: var(--color-accent, #1a3a5c);
}

.total-price {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-size: 1.1rem;
  padding: 0.8rem 1rem;
  border: 1px solid var(--color-light-shadow, #ddd);
  border-radius: var(--radius, 8px);
  margin-bottom: 1.2rem;
}

.total-price strong {
  font-size: 1.3rem;
  color: var(--color-accent, #1a3a5c);
}

.total-price small {
  font-size: 0.8rem;
  color: var(--color-fg-darker, #888);
}

.error-block {
  background: color-mix(in srgb, var(--color-error, #e74c3c) 15%, var(--color-bg-lighter, #fff));
  border: 1px solid var(--color-error, #e74c3c);
  border-radius: var(--radius, 8px);
  padding: 1rem 1.2rem;
  margin-bottom: 1rem;
  color: var(--color-error, #c0392b);
}

.warning-block {
  background: color-mix(in srgb, var(--color-accent, #f9a825) 15%, var(--color-bg-lighter, #fff));
  border: 1px solid var(--color-accent, #f9a825);
  border-radius: var(--radius, 8px);
  padding: 1rem 1.2rem;
  margin-bottom: 1rem;
  color: var(--color-fg, #795548);
}

.error-block ul {
  margin: 0.5rem 0;
  padding-left: 1.2rem;
}

.error-block a {
  color: var(--color-error, #c0392b);
  font-weight: 600;
}

.warning-block a {
  color: var(--color-accent, #795548);
  font-weight: 600;
}

.success-block {
  background: color-mix(in srgb, var(--color-success, #27ae60) 15%, var(--color-bg-lighter, #fff));
  border: 1px solid var(--color-success, #27ae60);
  border-radius: var(--radius, 8px);
  padding: 1rem 1.2rem;
  margin-bottom: 1rem;
  color: var(--color-success, #1e8449);
}

.back-actions {
  margin-top: 1.5rem;
}

.form-actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.btn-submit {
  padding: 0.7rem 1.5rem;
  background: var(--color-accent, #1a3a5c);
  color: var(--color-bg, #fff);
  border: none;
  border-radius: var(--radius, 8px);
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  padding: 0.7rem 1.2rem;
  background: transparent;
  border: 1px solid var(--color-light-shadow);
  color: var(--color-fg, #333);
  border-radius: var(--radius, 8px);
  cursor: pointer;
  font-size: 0.95rem;
}

.no-events,
.loading {
  text-align: center;
  padding: 2rem;
}

@media (max-width: 600px) {
  .summary,
  .confirmed-step,
  .payment-cta {
    padding: 1rem;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .pricing-grid {
    grid-template-columns: 1fr;
  }

  .existing-item {
    flex-direction: column;
    align-items: flex-start;
  }
}

.license-status {
  margin: 0.8rem 0;
  padding: 0.6rem;
  border-radius: 6px;
  font-size: 0.95rem;
}

.license-status.found {
  background: color-mix(in srgb, var(--color-success, #2e7d32) 15%, var(--color-bg-lighter, #fff));
  color: var(--color-success, #2e7d32);
}

.license-status.not-found {
  background: color-mix(in srgb, var(--color-accent, #f9a825) 15%, var(--color-bg-lighter, #fff));
  color: var(--color-accent, #e65100);
}

.license-status.error,
.license-status.unknown {
  background: var(--color-light-shadow, #f5f5f5);
  color: var(--color-fg-darker, #555);
}

.summary {
  background: var(--color-bg-lighter, #f8fafc);
  border: 1px solid var(--color-light-shadow, #e2e8f0);
  border-radius: 8px;
  padding: 1.5rem;
  color: var(--color-fg, #333);
}

.summary-player {
  border-bottom: 1px solid var(--color-light-shadow, #e2e8f0);
  padding: 1rem 0;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.4rem;
}

.person-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem 0.8rem;
}

.person-info {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.person-status {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.person-price {
  color: var(--color-accent, #1a3a5c);
}

.person-entries {
  margin: 0.35rem 0 0;
  padding-left: 1.1rem;
  font-size: 0.85rem;
  color: var(--color-fg-darker, #888);
}

.license-tag {
  font-size: 0.85rem;
  padding: 0.25rem 0.6rem;
  border-radius: 12px;
}

.license-tag.found {
  background: color-mix(in srgb, var(--color-success, #065f46) 20%, var(--color-bg-lighter, #fff));
  color: var(--color-success, #065f46);
}

.license-tag.not-found {
  background: color-mix(in srgb, var(--color-error, #991b1b) 20%, var(--color-bg-lighter, #fff));
  color: var(--color-error, #991b1b);
}

.partner {
  font-size: 0.9rem;
  color: var(--color-fg-darker, #555);
  margin-top: 0.3rem;
}

.price-line {
  margin-top: 0.6rem;
  font-weight: 600;
  text-align: right;
  color: var(--color-accent, #1a3a5c);
}

.summary-total {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--color-light-shadow, #e2e8f0);
  font-size: 1.2rem;
}

.summary-player.already-existed {
  opacity: 0.5;
  background: var(--color-light-shadow, #f5f5f5);
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
}

.license-tag.already-tag {
  background: var(--color-light-shadow, #e0e0e0);
  color: var(--color-fg-darker, #616161);
}

.info-block.all-registered {
  background: color-mix(in srgb, var(--color-accent, #f9a825) 15%, var(--color-bg-lighter, #fff));
  border: 1px solid var(--color-accent, #ffcc80);
  border-radius: var(--radius, 8px);
  padding: 0.9rem 1.1rem;
  margin-bottom: 1rem;
  color: var(--color-accent, #e65100);
  font-weight: 600;
}

.existing-inscriptions {
  background: color-mix(in srgb, var(--color-success, #2e7d32) 12%, var(--color-bg-lighter, #fff));
  border: 1px solid color-mix(in srgb, var(--color-success, #2e7d32) 40%, transparent);
  border-radius: var(--radius, 8px);
  padding: 1rem 1.2rem;
  margin-bottom: 1.2rem;
}

.existing-inscriptions h4 {
  margin: 0 0 0.6rem;
  color: var(--color-success, #2e7d32);
  font-size: 0.95rem;
}

.existing-item {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 0.6rem;
  align-items: center;
  padding: 0.4rem 0;
  font-size: 0.9rem;
  border-bottom: 1px solid color-mix(in srgb, var(--color-success, #2e7d32) 25%, transparent);
}

.existing-item:last-child {
  border-bottom: none;
}

.existing-name {
  font-weight: 600;
  color: var(--color-fg, #1b5e20);
}

.existing-cat {
  background: color-mix(in srgb, var(--color-success, #2e7d32) 25%, var(--color-bg-lighter, #fff));
  color: var(--color-success, #1b5e20);
  padding: 0.15rem 0.5rem;
  border-radius: 10px;
  font-size: 0.8rem;
}

.centered-block {
  text-align: center;
  padding: 3rem 1rem;
}

.existing-header {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 0.6rem;
}

.existing-header h4 {
  margin: 0;
  color: var(--color-success, #2e7d32);
  font-size: 0.95rem;
}

.existing-actions {
  margin-top: 0.8rem;
}

.confirmed-step {
  background: color-mix(in srgb, var(--color-success, #6ee7b7) 15%, var(--color-bg-lighter, #fff));
  border: 1px solid var(--color-success, #6ee7b7);
  border-radius: var(--radius, 8px);
  padding: 1.5rem;
  margin-bottom: 1.2rem;
}

.success-banner h3 {
  margin: 0 0 0.5rem;
  color: var(--color-success, #065f46);
}

.success-banner p {
  margin: 0.3rem 0;
  color: var(--color-success, #1e8449);
}

.confirmation-message {
  margin-top: 1rem;
  padding: 0.9rem 1rem;
  background: var(--color-bg-lighter, #fff);
  border-left: 4px solid var(--color-success, #065f46);
  border-radius: var(--radius, 8px);
}

.confirmation-message p {
  margin: 0;
  color: var(--color-fg, #222);
  /* Le texte est saisi dans un textarea : on respecte les retours à la ligne. */
  white-space: pre-line;
}

/* Liens du message : même jaune que l'adresse dans la fiche tournoi, sans soulignement. */
.confirmation-message a {
  color: var(--color-accent);
  text-decoration: none;
  overflow-wrap: anywhere;
}

.confirmation-message a:hover {
  opacity: 0.8;
}

.payment-hint {
  font-size: 0.88rem;
  color: var(--color-fg-darker, #555);
  margin-top: 0.5rem !important;
}

.existing-cat .seeking {
  color: var(--color-fg-darker, #888);
  font-style: italic;
}
</style>